import { createSelectorCreator, createSelector, defaultMemoize } from "reselect";
import { mapFiltersSelector } from "orion-components/ContextPanel/Selectors";
import { contextById } from "orion-components/ContextualData/Selectors";
import { userIntsByEntityTypeSelector } from "orion-components/Session/Selectors";

import isEqual from "react-fast-compare";
import merge from "lodash/merge";
import cloneDeep from "lodash/cloneDeep";
import keyBy from "lodash/keyBy";
import pickBy from "lodash/pickBy";
import each from "lodash/each";
import { globalGeo, userIntegrations } from "../../GlobalData/Selectors";

export { getFacilities } from "orion-components/Map/Selectors/FacilitiesSelectors";
export { overlayLayerSelector } from "orion-components/Map/Selectors/MapOverlaySelector";
export { floorPlanDevicesSelector } from "./DeviceLayerSelectors";

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

const cameraProps = (state) => {
	const cameraInts = userIntsByEntityTypeSelector(state)("camera")?.map((int) => int.feedId) || [];
	let cameras = {};
	cameraInts.forEach((id) => {
		cameras = merge(cameras, cloneDeep(state.globalData[id]));
	});
	return cameras && cameras.data ? cameras.data : {};
};

const deviceProps = (state) => {
	const deviceInts = userIntsByEntityTypeSelector(state)("device")?.map((int) => int.feedId) || [];
	let devices = {};
	deviceInts.forEach((id) => {
		devices = merge(devices, cloneDeep(state.globalData[id]));
	});
	return devices && devices.data ? devices.data : {};
};

const floorPlan = (state) => state.floorPlan;

export const floorPlanSelector = createSelector(floorPlan, (floorPlan) => {
	const {
		selectedFloor,
		coordinates,
		creating,
		preLoaded,
		image,
		devicePlacementModeEnabled,
		pendingDevicePlacements
	} = floorPlan || {};

	return {
		selectedFloor,
		coordinates,
		creating,
		image,
		preLoaded,
		devicePlacementModeEnabled,
		pendingDevicePlacements
	};
});

const entityGeoSelector = (entityType) =>
	createDeepEqualSelector(globalGeo, userIntegrations, (globalGeo, integrations) => {
		let entities = {};
		each(
			pickBy(
				integrations,
				(integration) => integration.entityType === entityType && globalGeo[integration.feedId]
			),
			(integration) => {
				entities = {
					...entities,
					...globalGeo[integration.feedId].data
				};
			}
		);
		return entities;
	});

const cameraGeo = entityGeoSelector("camera");

export const cameraMapFeatures = (id) => {
	return createDeepEqualSelector(
		contextById(id),
		cameraGeo,
		cameraProps,
		mapFiltersSelector,
		(context, cameraGeo, cameraProps, filters) => {
			let mapFeatures = {};
			if (context && context.entity) {
				const { fov, fovItems, entity, fovEvents, linkedEntities } = context;
				mapFeatures = {
					[entity.id]: entity
				};
				if (linkedEntities) {
					mapFeatures = {
						...mapFeatures,
						...keyBy(linkedEntities, "id")
					};
				}
				if (fovItems) {
					mapFeatures = { ...mapFeatures, ...keyBy(fovItems, "id") };
				}
				if (fovEvents) {
					mapFeatures = { ...mapFeatures, ...keyBy(fovEvents, "id") };
				}
				if (fov && !Array.isArray(fov)) {
					mapFeatures = { ...mapFeatures, [fov.id]: fov };
				}
			} else {
				const cameras = cameraGeo ? merge(cameraProps, cameraGeo) : cameraProps;
				if (filters && Object.keys(filters).length) {
					mapFeatures = pickBy(cameras, (camera) => Object.keys(filters).includes(camera.id));
				} else {
					mapFeatures = cameras;
				}
			}

			//Filter out non-map display types
			const filteredMapFeatures = {};
			Object.keys(mapFeatures).forEach((key) => {
				if ((mapFeatures[key].entityData.displayType || "map").toLowerCase() === "map") {
					filteredMapFeatures[key] = mapFeatures[key];
					if (!mapFeatures[key].entityData.properties.entityType) {
						filteredMapFeatures[key].entityData.properties["entityType"] =
							filteredMapFeatures[key].entityType;
					}
				}
			});

			return filteredMapFeatures;
		}
	);
};

const deviceGeo = entityGeoSelector("device");

export const deviceMapFeatures = (id) => {
	return createDeepEqualSelector(
		contextById(id),
		deviceGeo,
		deviceProps,
		mapFiltersSelector,
		(context, deviceGeo, deviceProps, filters) => {
			let mapFeatures = {};
			if (context && context.entity) {
				const { fov, fovItems, entity, fovEvents, linkedEntities } = context;
				mapFeatures = {
					[entity.id]: entity
				};
				if (linkedEntities) {
					mapFeatures = {
						...mapFeatures,
						...keyBy(linkedEntities, "id")
					};
				}
				if (fovItems) {
					mapFeatures = { ...mapFeatures, ...keyBy(fovItems, "id") };
				}
				if (fovEvents) {
					mapFeatures = { ...mapFeatures, ...keyBy(fovEvents, "id") };
				}
				if (fov && !Array.isArray(fov)) {
					mapFeatures = { ...mapFeatures, [fov.id]: fov };
				}
			} else {
				const devices = deviceGeo ? merge(deviceProps, deviceGeo) : deviceProps;
				if (filters && Object.keys(filters).length) {
					mapFeatures = pickBy(devices, (device) => Object.keys(filters).includes(device.id));
				} else {
					mapFeatures = devices;
				}
			}

			//Filter out non-map display types
			const filteredMapFeatures = {};
			Object.keys(mapFeatures).forEach((key) => {
				if ((mapFeatures[key].entityData.displayType || "map").toLowerCase() === "map") {
					filteredMapFeatures[key] = mapFeatures[key];
					if (!mapFeatures[key].entityData.properties.entityType) {
						filteredMapFeatures[key].entityData.properties["entityType"] =
							filteredMapFeatures[key].entityType;
					}
				}
			});

			return filteredMapFeatures;
		}
	);
};
